import {
  AgxButton,
  AgxCollapsible,
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
  toDateOnlyString,
} from '@urbanx/agx.ui-components2';
import { Deposit, FormCondition, Condition } from '@urbanx/agx.campaigns.client';
import ConditionDate from '../components/ConditionDate';
import { useFormConditionDueDate } from '../components/useFormConditionDueDate';
import slugify from 'slugify';
import './ConditionDatePicker.scss';

interface ConditionPanelProps {
  id: string;
  panelTitle: string;
  contractDate?: Date;
  condition?: FormCondition;
  onChange: (condition: FormCondition | Deposit | undefined) => void;
  onDelete?: () => void;
  hideEditButton?: boolean;
  hideConditionInfo?: boolean;
  hideDeleteButton?: boolean;
  specialConditionIdToClauseNumberMap: {
    [key: string]: string;
  };
}

export function ConditionPanel({
  id,
  panelTitle,
  contractDate,
  condition,
  onChange,
  onDelete,
  hideEditButton,
  hideConditionInfo,
  hideDeleteButton,
  specialConditionIdToClauseNumberMap,
}: ConditionPanelProps) {
  const dueDate = useFormConditionDueDate(condition, contractDate);

  if (
    !condition?.condition ||
    [Condition.NotSubjectToFinance, Condition.NotApplicable].includes(condition.condition)
  )
    return;

  const dueDateValue = toDateOnlyString(dueDate?.dueDate) ?? '';
  return (
    <AgxCollapsible
      key={`deposit-panel-${id}`}
      variant={'withDivider'}
      enableToggle
      open
      customTitle={
        <AgxSpacer fill direction="horizontal" gap="xs" contentAlignment="center">
          <AgxTypography
            id={`condition-${slugify(panelTitle)}`}
            type={'title'}
            size={'large'}
            color="var(--text-primary)"
          >
            {panelTitle}
          </AgxTypography>
        </AgxSpacer>
      }
      content={
        <>
          {!hideConditionInfo && condition && (
            <>
              <AgxSpacer
                direction="horizontal"
                gap="spacing-md"
                fill
                padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
              >
                <ConditionDate
                  id={id}
                  condition={condition}
                  specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
                />
              </AgxSpacer>
              <div className="condition-panel-due-on-divider">
                <AgxDivider variant="horizontal" />
              </div>
            </>
          )}
          <AgxSpacer
            direction="vertical"
            gap="spacing-xl"
            fill
            padding={{ top: 'spacing-3xl', bottom: 'spacing-3xl' }}
            contentAlignment="start"
          >
            <AgxDatePicker
              id={`${id}-due-date`}
              className="contract-information-date-picker"
              disabled={hideEditButton}
              optionalText=""
              label="Due Date"
              key={dueDateValue}
              value={dueDateValue}
              onChange={(value) => {
                if (!value) return;

                onChange({
                  ...condition,
                  condition: Condition.OnSpecificDate,
                  referenceDate: toDateOnlyString(value),
                  daysFromDate: undefined,
                  conditionType: undefined,
                  specialConditionId: undefined,
                });
              }}
            />
            {!hideDeleteButton && (
              <AgxButton
                id={`${id}-delete`}
                variant="link"
                danger
                text="Delete"
                onClick={onDelete}
              />
            )}
          </AgxSpacer>
        </>
      }
    ></AgxCollapsible>
  );
}
